import { DateTime } from 'luxon';
import { FoodHeader, Food, FoodSource, getAnyFoodUsedDate, getFoodSource, isLogMeal } from '../data-model/food';
import { AltNameMatch, analyzeFoodText, compareRankedFoods, scoreFoodMatch, FoodNameMatch, FoodTextAnalyzerDeps } from './food-search-match';
import { effectiveTierOf } from './food-search-tiers';
import { CompletedFoodQuery } from './food-word-completion';

/** What `rankFoodHeaders` needs to know about the search it is ordering. */
export interface FoodRankingOptions {
  /** the current time, in UTC; decides how recently each food was used */
  now: DateTime;
  /** the search text as typed; an empty text means "show my history" */
  text: string;
  /** the typed words, already finished off against the food word dictionary */
  completedQuery: CompletedFoodQuery;
  /** the analyzer the matcher compares food names with */
  analyzerDeps: FoodTextAnalyzerDeps;
  /** whether foods are ordered by source tier and fewer extra words; off only for the empty search box */
  rankBySourceTiers: boolean;
  /** whether this is a barcode search, whose results answer no typed word */
  isBarcodeSearch: boolean;
  /** whether meals logged in the last 2 days are lifted above the foods in them (the Foods page's
   *  "all types" view) */
  boostRecentLogMeals: boolean;
  /** whether each food's description is replaced with how it was scored (a debug setting) */
  debugDescriptions: boolean;
  /**
   * Names and aliases already split and stemmed, by food id (food staples plan 4c, #5625).
   *
   * The cost control behind searching the staples on the device: an offline search ranks every
   * staple the device holds, and analysing each of their names and aliases again on every
   * keystroke is the bulk of what that costs. A food with no entry is analysed exactly as
   * before, so leaving this out changes nothing.
   *
   * Each entry names the DOCUMENT it was read from, and is used only for that document. The id
   * alone would not do: the person's own cached copy of a staple carries the same id and can be
   * older, and so can the server's copy, and scoring a row against a name it does not have
   * changes which foods the list shows with nothing to say so.
   */
  preparedWords?: ReadonlyMap<string, { doc: unknown; name: string[]; aliases: string[][] }>;
}

/**
 * Puts the foods a search found in the order the food list shows them (#5410, #5550, #5590).
 *
 * Scores every header on how well its names answer the typed text and how recently it was used,
 * drops the device foods that answer none of the typed words, drops server results that are also
 * on the device, and sorts with `compareRankedFoods`. The headers are updated in place with the
 * values the sort reads (`match`, `answeredWords`, `exactWords`, `tier`, `extraWords`, `age`).
 *
 * The Foods page calls this to order its list. The food scanners call it to score their candidates,
 * then pick with their own rule (`findRankedFoodMatchAsync` in `scanned-food-match.ts`).
 */
export function rankFoodHeaders(headers: FoodHeader[], options: FoodRankingOptions): FoodHeader[] {
  const { now, text, completedQuery, analyzerDeps, rankBySourceTiers } = options;
  const textWords = completedQuery.terms;

  // score every food on how well its name answers the typed text and how recently it was
  // used, and drop the local ones whose name answers none of the typed words
  const preparedHeaders: FoodHeader[] = [];
  for (const foodHeader of headers) {

    // --------------- calculate the last used time ---------------

    const usedTime_utc = foodHeader.cachedFood && getAnyFoodUsedDate(foodHeader.cachedFood);
    const foodIsLiked = foodHeader.cachedFood && (foodHeader.cachedFood as Food)?.local?.liked > 0;
    // real days since the food was last used, for the history tier; `age` below is bent for sorting
    const daysSinceUse = usedTime_utc ? now.diff(DateTime.fromISO(usedTime_utc)).as('days') : undefined;
    // if food was used before
    if (usedTime_utc) {
      // calculate its age for sorting
      foodHeader.age = now.diff(DateTime.fromISO(usedTime_utc)).as('seconds') / (24 * 3600); // in fractional days
      // when showing recent meals with "all" filter, boost the age of the meals in the last 2 days
      //  to make them appear in front of the foods in them
      if (options.boostRecentLogMeals && isLogMeal(foodHeader.cachedFood) && foodHeader.age < 2) {
        foodHeader.age -= 2;
      }

      // normalize for the -2 hack above, since it has to be above 0 due to 1/x below
      foodHeader.age = Math.max(foodHeader.age + 2, 0);

      // if food wasn't used or liked, and this is not an explicit search
    } else if (!text && !foodIsLiked) {
      // then don't show this food at all (we want to show only "history" in that case)
      continue;

      // if it was searched for, but never used, sort it last
    } else {
      foodHeader.age = 999;
    }

    const newestWeight = 1;
    const oldestWeight = 0.3;
    const ageRatio = 1 / Math.max(foodHeader.age, 1); // by this newest foods are mapped to 1, very old (or never used) are mapped towards 0
    let ageWeight = (newestWeight - oldestWeight) * ageRatio + oldestWeight; // this remaps 1 to newest and 0 to oldest

    // special extra de-boost for foods that were never used
    if (foodHeader.age === 999 && !foodIsLiked) {
      ageWeight = 0.2;
    }
    // extra boost for foods that are liked
    if (foodIsLiked) {
      ageWeight = Math.min(ageWeight * 2, 1);
    }

    // --------------- how well the name answers what was typed ---------------
    // Already split and stemmed for this food, when the caller had it to hand; see
    // `preparedWords` above for why that is worth doing, and why the entry is taken only when it
    // was read from the VERY DOCUMENT this row carries rather than merely from the same id.
    const preparedForId = options.preparedWords?.get(foodHeader._id);
    const prepared = preparedForId?.doc === foodHeader.cachedFood ? preparedForId : undefined;
    const nameWords = prepared?.name ?? analyzeFoodText(foodHeader.name, analyzerDeps);
    // a staple's aliases are the plain words people search, so while source tiers are on a match
    // on one counts as much as its name (food staples plan 3); every other food keeps the discount
    const isStapleRankedByAlias = rankBySourceTiers && getFoodSource(foodHeader._id) === FoodSource.STP;
    const altNameMatch = isStapleRankedByAlias ? AltNameMatch.SameAsName : AltNameMatch.Discounted;
    // the other names this food is known by, which the server searches too. A locally cached food
    // has them on its full document; a server result carries them on its header, read only for such
    // a staple, so every other server result is ranked exactly as before. A logged meal carries the
    // names of the foods inside it on its header (recentMealSearchNames), so typing a food finds the
    // meal that holds it (#5246)
    const readsHeaderAltNames = isStapleRankedByAlias || (!!foodHeader.cachedFood && isLogMeal(foodHeader.cachedFood));
    const altNames = (foodHeader.cachedFood as Food)?.altNames ?? (readsHeaderAltNames ? foodHeader.altNames : undefined);
    // an EMPTY search box means "show me my history": every cached food qualifies and the age
    // weighting below is left to sort by what was eaten recently. Asked of the matcher this
    // would be indistinguishable from someone typing only punctuation, which must match
    // nothing at all - so the question is answered here, where the raw text can be seen.
    // It also skips reading the alternative names, which nothing would look at on this path.
    const nameMatch: FoodNameMatch = text.length === 0
      ? { score: 1, answeredWords: 0, exactWords: 0, extraWords: 0 }
      : scoreFoodMatch(textWords, nameWords, altNames, analyzerDeps,
        completedQuery.lastTermAlternatives, altNameMatch, prepared?.aliases);
    const totalMatch = nameMatch.score;
    if (options.debugDescriptions) {
      const matchedWords = nameMatch.matchedAltName
        ? `alt: ${nameMatch.matchedAltName}`
        : nameWords.join(',');
      foodHeader.description = `match ${totalMatch.toFixed(6)}, ` +
        `score: ${foodHeader.score?.toFixed(6) || 'N/A'} age: ${foodHeader.age.toFixed(2)} days, ageWeight: ${ageWeight.toFixed(4)} (${matchedWords})`;
    }

    // drop a locally cached food that answers NONE of the typed words (#5114). Server results
    // are never dropped here - the server already decided they were worth returning, and this
    // score only orders them.
    //
    // There is no tuned threshold any more. The rule this replaced scored on shared
    // three-letter fragments, so every name containing "ch" scored a little for "chai" and a
    // cut-off had to be guessed at; it was 0.2, and it let Cheddar Cheese through. Whole words
    // either answer the search or they do not.
    if (!options.isBarcodeSearch && !foodHeader.score && totalMatch === 0) {
      continue;
    }
    foodHeader.match = totalMatch * ageWeight;
    // how much of what was typed this food accounts for, which decides the order before
    // anything else does (#5410)
    foodHeader.answeredWords = nameMatch.answeredWords;
    // and how much of that it answered as typed, which orders the foods that answer the same
    // amount -- before how recently the food was logged gets a say (#5410)
    foodHeader.exactWords = nameMatch.exactWords;
    // and, if source tiers are switched on, where its source puts it (a food with no server score
    // is on the device) and how much of its name is about something else, so "Oatmeal" goes above
    // "Oatmeal Pie" (#5550, #4970); both left unset when off, which makes every food tie on them
    foodHeader.tier = rankBySourceTiers ? effectiveTierOf(foodHeader._id, foodHeader.score === undefined, daysSinceUse) : undefined;
    foodHeader.extraWords = rankBySourceTiers ? nameMatch.extraWords : undefined;

    preparedHeaders.push(foodHeader);
  }

  // ----------------- sort the headers by both match and score -----------------

  // separate them into two groups: global headers (in the cloud) and local headers (in the local cache) - the locals don't have `score` (but have `match`)
  let globalHeaders = preparedHeaders.filter(h => h.score !== undefined);
  const localHeaders = preparedHeaders.filter(h => h.score === undefined);
  // remove all global ones that have local versions
  const localIds = new Set(localHeaders.map(h => h._id));
  globalHeaders = globalHeaders.filter(h => !localIds.has(h._id));

  // one list, ordered by how much of the typed text each food answers first and by the
  // recency-weighted match inside each group (#5410). See compareRankedFoods for why a food
  // logged last week must not beat a far better name match.
  return [...globalHeaders, ...localHeaders].sort(compareRankedFoods);
}
